import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { Avatar, AvatarFallbackText } from "@/components/ui/avatar"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Progress, ProgressFilledTrack } from "@/components/ui/progress"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { sharePassportLink } from "@/services/passportShare"
import { createProfileInvitation } from "@/services/profileInvitations"
import {
  buildPublicPassportUrl,
  resolvePublicPassportCard,
  type PublicPassportCard,
} from "@/services/publicPassport"
import { router, useLocalSearchParams } from "expo-router"
import { Award, Send, Share2, ShieldCheck, Trophy, UserPlus } from "lucide-react-native"
import { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Platform, TextInput, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function PublicPassportScreen() {
  const auth = useAuth()
  const params = useLocalSearchParams<{ shareKey?: string }>()
  const shareKey = useMemo(() => String(params.shareKey ?? ""), [params.shareKey])
  const [card, setCard] = useState<PublicPassportCard | null>(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState("")
  const [projectName, setProjectName] = useState("EQ collaboration")
  const [roleTitle, setRoleTitle] = useState("Conversation partner")
  const [inviteMessage, setInviteMessage] = useState("")
  const [inviteStatus, setInviteStatus] = useState("")
  const [inviteLoading, setInviteLoading] = useState(false)
  const scrollY = useRef(new Animated.Value(0)).current

  useEffect(() => {
    let mounted = true

    async function loadCard() {
      setLoading(true)
      setMessage("")
      try {
        const result = await resolvePublicPassportCard(shareKey)
        if (!mounted) return
        setCard(result)
        setMessage(result ? "" : "This Passport link is unavailable or not public.")
      } catch (error) {
        if (!mounted) return
        setMessage(error instanceof Error ? error.message : String(error))
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadCard()

    return () => {
      mounted = false
    }
  }, [shareKey])

  async function sharePassport() {
    if (!card) return

    const url = buildPublicPassportUrl(card.profile.shareKey)
    await sharePassportLink({
      title: "EMO.coach Passport",
      message: `${card.displayName}'s EMO.coach Passport\n${url}`,
      url,
    })
  }

  async function sendInvitation() {
    if (!card) return
    if (!auth.session) {
      router.push("/auth")
      return
    }

    setInviteLoading(true)
    setInviteStatus("")

    try {
      const invitation = await createProfileInvitation({
        recipientUserId: card.id,
        recipientShareKey: card.profile.shareKey,
        senderDisplayName:
          auth.profile?.display_name ?? auth.user?.email?.split("@")[0] ?? "EMO.coach member",
        recipientDisplayName: card.displayName,
        invitationKind: "project",
        projectName,
        roleTitle,
        projectDescription:
          "A collaboration request sent from a shared EMO.coach Passport public profile.",
        timeline: "This week",
        message:
          inviteMessage.trim() ||
          `I saw your EMO.coach Passport and would like to collaborate on ${projectName}.`,
      })

      setInviteStatus(`Invitation ${invitation.id.slice(0, 8)} sent.`)
      setInviteMessage("")
    } catch (error) {
      setInviteStatus(error instanceof Error ? error.message : String(error))
    } finally {
      setInviteLoading(false)
    }
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Public Passport"
        title={card?.displayName ?? "EMO.coach Passport"}
        subtitle={card?.profile.headline ?? "Checking the public Passport link."}
        backFallbackHref="/"
        statusText={loading ? "Loading" : card ? "Public" : "Unavailable"}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 64 : 48,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        {loading ? (
          <PassportState
            title="Loading Passport"
            description="Checking the public Passport link."
          />
        ) : card ? (
          <VStack className="gap-5">
            <Box
              className="rounded-3xl border p-6"
              style={{
                backgroundColor: `${Colors.dark.primary}14`,
                borderColor: `${Colors.dark.primary}55`,
              }}
            >
              <VStack className="items-center">
                <Avatar size="2xl" className="border" style={{ borderColor: Colors.dark.primary }}>
                  <AvatarFallbackText>{card.displayName}</AvatarFallbackText>
                </Avatar>
                <Text className="mt-4 text-center text-3xl font-bold text-white">
                  {card.displayName}
                </Text>
                <Text className="mt-1 text-center font-bold" style={{ color: Colors.dark.primary }}>
                  {card.profile.headline}
                </Text>
                <Text
                  className="mt-3 text-center text-sm leading-5"
                  style={{ color: Colors.dark.textSecondary }}
                >
                  {card.profile.bio}
                </Text>
              </VStack>
            </Box>

            <Box
              className="rounded-3xl border p-5"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: `${Colors.dark.growth}44`,
              }}
            >
              <HStack className="mb-4 items-center gap-3">
                <ShieldCheck color={Colors.dark.growth} size={22} />
                <Text className="text-xl font-bold text-white">Verified EQ Progress</Text>
              </HStack>
              <Progress size="md" value={card.summary.progressPercent}>
                <ProgressFilledTrack style={{ backgroundColor: Colors.dark.growth }} />
              </Progress>
              <HStack className="mt-4 flex-wrap gap-3">
                <PublicStat label="Skills" value={card.summary.skillCount} />
                <PublicStat label="Verified levels" value={card.summary.verifiedLevelCount} />
                <PublicStat label="Proofs" value={card.summary.proofCount} />
                <PublicStat label="Public proofs" value={card.summary.publicProofCount} />
              </HStack>
            </Box>

            <Box
              className="rounded-3xl border p-5"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: `${Colors.dark.proof}44`,
              }}
            >
              <HStack className="mb-4 items-center gap-3">
                <Trophy color={Colors.dark.proof} size={22} />
                <Text className="text-xl font-bold text-white">Key Proofs</Text>
              </HStack>
              {card.keyProofs.length > 0 ? (
                <VStack className="gap-3">
                  {card.keyProofs.map((proof) => (
                    <Box
                      key={proof.id}
                      className="rounded-2xl border p-4"
                      style={{
                        backgroundColor: `${Colors.dark.proof}12`,
                        borderColor: `${Colors.dark.proof}38`,
                      }}
                    >
                      <Text className="font-bold text-white">{proof.title}</Text>
                      <Text
                        className="mt-1 text-sm leading-5"
                        style={{ color: Colors.dark.textSecondary }}
                      >
                        {proof.domainName} / {proof.skillName} / Level {proof.checkpointLevel}
                      </Text>
                      <Text className="mt-2 text-xs font-bold" style={{ color: Colors.dark.proof }}>
                        {proof.evidenceCount} evidence reference
                        {proof.evidenceCount === 1 ? "" : "s"}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <PassportState
                  compact
                  title="No public proofs yet"
                  description="Only verified proofs marked public appear on a shared Passport."
                />
              )}
            </Box>

            <Box
              className="rounded-3xl border p-5"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: `${Colors.dark.primary}44`,
              }}
            >
              <HStack className="mb-4 items-center gap-3">
                <UserPlus color={Colors.dark.primary} size={22} />
                <VStack className="flex-1">
                  <Text className="text-xl font-bold text-white">Invite to collaborate</Text>
                  <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                    Sends a Supabase-backed invitation to this Passport owner.
                  </Text>
                </VStack>
              </HStack>

              <VStack className="gap-3">
                <TextInput
                  value={projectName}
                  onChangeText={setProjectName}
                  placeholder="Project or team"
                  placeholderTextColor={Colors.dark.textTertiary}
                  className="rounded-2xl border px-4 py-3 text-white"
                  style={{
                    backgroundColor: Colors.dark.surface,
                    borderColor: "rgba(255,255,255,0.08)",
                  }}
                />
                <TextInput
                  value={roleTitle}
                  onChangeText={setRoleTitle}
                  placeholder="Role"
                  placeholderTextColor={Colors.dark.textTertiary}
                  className="rounded-2xl border px-4 py-3 text-white"
                  style={{
                    backgroundColor: Colors.dark.surface,
                    borderColor: "rgba(255,255,255,0.08)",
                  }}
                />
                <TextInput
                  multiline
                  value={inviteMessage}
                  onChangeText={setInviteMessage}
                  placeholder="Short message"
                  placeholderTextColor={Colors.dark.textTertiary}
                  className="min-h-24 rounded-2xl border px-4 py-3 text-white"
                  style={{
                    backgroundColor: Colors.dark.surface,
                    borderColor: "rgba(255,255,255,0.08)",
                    textAlignVertical: "top",
                  }}
                />
                {inviteStatus ? (
                  <Text className="text-sm font-bold" style={{ color: Colors.dark.proof }}>
                    {inviteStatus}
                  </Text>
                ) : null}
                <Button
                  size="xl"
                  className="rounded-2xl"
                  disabled={inviteLoading}
                  style={{
                    backgroundColor: Colors.dark.primary,
                    opacity: inviteLoading ? 0.72 : 1,
                  }}
                  onPress={sendInvitation}
                >
                  <Send color={Colors.dark.primaryForeground} size={18} />
                  <ButtonText
                    className="font-bold"
                    style={{ color: Colors.dark.primaryForeground }}
                  >
                    {inviteLoading ? "Sending" : "Send Invitation"}
                  </ButtonText>
                </Button>
              </VStack>
            </Box>

            <Button
              size="xl"
              className="rounded-2xl"
              style={{ backgroundColor: Colors.dark.primary }}
              onPress={sharePassport}
            >
              <Share2 color={Colors.dark.primaryForeground} size={18} />
              <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
                Share
              </ButtonText>
            </Button>
          </VStack>
        ) : (
          <PassportState title="Passport unavailable" description={message} />
        )}
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function PublicStat({ label, value }: { label: string; value: number }) {
  return (
    <Box
      className="min-w-[46%] flex-1 rounded-2xl border p-4"
      style={{
        backgroundColor: `${Colors.dark.growth}12`,
        borderColor: `${Colors.dark.growth}33`,
      }}
    >
      <Text className="text-2xl font-bold text-white">{value}</Text>
      <Text className="text-xs font-bold uppercase" style={{ color: Colors.dark.textSecondary }}>
        {label}
      </Text>
    </Box>
  )
}

function PassportState({
  title,
  description,
  compact,
}: {
  title: string
  description: string
  compact?: boolean
}) {
  return (
    <Box
      className={`${compact ? "rounded-2xl p-4" : "rounded-3xl p-6"} border`}
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <VStack className="items-center gap-3">
        <View
          className="h-12 w-12 items-center justify-center rounded-full"
          style={{ backgroundColor: `${Colors.dark.primary}24` }}
        >
          <Award color={Colors.dark.primary} size={22} />
        </View>
        <Text className="text-center text-xl font-bold text-white">{title}</Text>
        <Text
          className="text-center text-sm leading-5"
          style={{ color: Colors.dark.textSecondary }}
        >
          {description}
        </Text>
      </VStack>
    </Box>
  )
}
